import { connect, useDispatch } from 'react-redux';
import { FormSpy } from 'react-final-form';
import { FieldArray, FieldArrayRenderProps } from 'react-final-form-arrays';
import { v4 as uuidv4 } from 'uuid';
import { Dispatch, useCallback, useMemo, useState } from 'react';

import { getFieldArrayErrorMessage } from 'CorePackage/MainModule/common/getFieldArrayErrorMessage';
import { PM_OBJECTS } from 'CorePackage/MainModule/routes/routes';
import { OBJECTS_BLOCK_ID } from 'KnmPmPackage/MainModule/constants/knmPmFieldsElementsIds';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import {
  getKnmFormRef,
  getKnmErknmSubjectTypesSelectOptions,
  getKnmErknmSubjectKindsSelectOptions,
  getKnmErknmSubjectSubKindsSelectOptions,
  getKnmErknmRiskCategoriesSelectOptions,
  getTargetEntitySelectOptions,
  getKnmSystemType,
  getKnmErknmDangerClassesSelectOptions,
  getKnmIsCanRefreshControlObject,
  getKnmIsChangeSubjectTypeFromActionsAvailable,
  getKnmKnoSupervisionUuid,
  getKnmOrderVersionId,
  getKnmPmReasonsFormValue,
  getKnmAdminCanEditSubject,
} from 'KnmPackage/MainModule/selectors/knmSelectors';
import { IKnmErknmFormObject } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormObject';
import { KnmObjectItem } from 'KnmPackage/KnmObjectsModule/components/KnmObjectItem';
import { Button } from 'UIPackage/MainModule/components/Button';
import { IDangerClass248 } from 'APIPackage/DictionariesModule/interfaces/IDangerClass248';
import { IRiskCategory248 } from 'APIPackage/DictionariesModule/interfaces/IRiskCategory248';
import { ISubjectKind } from 'APIPackage/DictionariesModule/interfaces/ISubjectKind';
import { ISubjectType } from 'APIPackage/DictionariesModule/interfaces/ISubjectType';
import { IDictionarySelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySelectOption';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { AdminSettingCodesEnum } from 'APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { isLaterThanAdminSetting } from 'CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { getAdminFetchSettings } from 'PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { IAdminSetting } from 'APIPackage/AdminModule/interfaces/IAdminSetting';
import { Tooltip } from 'UIPackage/MainModule/components/Tooltip';
import { LawInterfacesEnum } from 'CorePackage/MainModule/enums/LawInterfacesEnum';
import { getLawInterface } from 'CorePackage/MainModule/reducers/coreReducer';
import { findRegionSelectOption } from 'KnmPackage/MainModule/components/regions';
import { ICommonFormRef } from 'KnmErknmPackage/MainModule/interfaces/ICommonFormRef';
import { usePmErknmSubjectFields } from 'KnmPmPackage/PmSubjectModule/hooks/usePmErknmSubjectFields';
import { clearPmSubjectFields } from 'KnmPmPackage/PmSubjectModule/common/clearPmSubjectFields';
import { useKnmSetLegalEntityDataAction } from 'KnmPackage/MainModule/hooks/useKnmSetLegalEntityDataAction';
import { IDictionarySubjectTypeSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySubjectTypeSelectOption';
import {
  dictionariesSubjectKindsItemGet,
  dictionariesSubjectSubKindsItemGet,
} from 'CorePackage/DictionariesModule/actions/dictionariesActions';
import { KnmReasonTypeNameEnum } from 'KnmPackage/MainModule/enums/KnmReasonTypeNameEnum';
import { SelectPrescriptionNotice } from 'KnmErknmPackage/MainModule/components/SelectPrescriptionNotice';

import styles from './PmObjects.module.scss';
import { KnmErvkObjectsModal } from '../../../KnmPackage/KnmObjectsModule/components/KnmErvkObjectsModal';
import { IErvkObjectsOfControl } from '../../../APIPackage/KnmModule/interfaces/IErvkObjectsOfControl';
import { IErvkObjectsOfControlSubjectData } from '../../../APIPackage/KnmModule/interfaces/IErvkObjectsOfControlSubjectData';
import { NoticeTypesEnum } from '../../../CorePackage/NotificationModule/enums/NoticeTypesEnum';
import { MessageTypesEnum } from '../../../CorePackage/NotificationModule/enums/MessageTypesEnum';
import { RootActions } from '../../../CorePackage/MainModule/types/RootActions';
import { INotification } from '../../../CorePackage/NotificationModule/interfaces/INotification';
import { addNoticeAction } from '../../../CorePackage/NotificationModule/actions/NoticeActions';
import { ITargetEntity } from '../../../APIPackage/DictionariesModule/interfaces/ITargetEntity';
import { KnmSystemTypesEnum } from '../../../KnmPackage/MainModule/enums/KnmSystemTypesEnum';
import { getKnoSupervisionHazardClassApplies } from '../../../CorePackage/DictionariesModule/selectors/dictionariesSelectors';
import { IKnmFormSubject } from '../../../KnmPackage/KnmSubjectModule/interfaces/IKnmFormSubject';
import { mapSubjectTypeCodeToName } from '../../../KnmPackage/KnmObjectsModule/common/mapSubjectTypeCodeToName';
import { addObject } from '../../../KnmPackage/KnmObjectsModule/common/addObject';
import { addErvkObject } from '../../../KnmPackage/KnmObjectsModule/common/addErvkObject';
import { ICountrySelectOption } from '../../../CorePackage/CatalogsModule/interfaces/ICountrySelectOption';
import {
  getCountriesSelectOptions,
  getDistrictSelectOptions,
} from '../../../CorePackage/CatalogsModule/selectors/catalogsSelectors';
import { KnmSubjectTypesEnum } from '../../../KnmErknmPackage/ErknmSubjectModule/components/ErknmSubjectInfo';
import { getObjectBlockId } from '../../../KnmPackage/MainModule/constants/fieldsElementsIds';
import { getTitleFieldWithRedStar } from '../../../functions/getTitleFieldWithRedStar';
import { IKnmDistrictSelectOption } from '../../../CorePackage/CatalogsModule/interfaces/IKnmDistrictSelectOption';

interface IPmObjectsProps {
  className?: string;
  isLinkedByImpossibility?: boolean;
  watchOnly: boolean;
}

interface IPmObjectsStoreProps {
  countriesSelectOptions: ICountrySelectOption[] | undefined;
  dangerClassesSelectOptions: IDictionarySelectOption<IDangerClass248>[] | undefined;
  formRef: IKnmFormRef | undefined;
  hazardClassApplies: boolean | undefined;
  knmSystemType?: KnmSystemTypesEnum;
  objectKindsSelectOptions: IDictionarySelectOption<ISubjectKind>[] | undefined;
  objectSubKindsSelectOptions: IDictionarySelectOption<ISubjectKind>[] | undefined;
  objectTypesSelectOptions: IDictionarySelectOption<ISubjectType>[] | undefined;
  riskCategoriesSelectOptions: IDictionarySelectOption<IRiskCategory248>[] | undefined;
  subjectTypeOptions: IDictionarySelectOption<ITargetEntity>[] | undefined;
  isCanRefreshControlObject?: boolean | undefined;
  settings: IAdminSetting[] | undefined;
  isChangeSubjectTypeFromActionsAvailable: boolean;
  isErknm: boolean;
  districtSelectOptions: IKnmDistrictSelectOption[] | undefined;
  knoSupervisionUuid?: string;
  orderVersionId?: string;
  reasonUuid?: string;
  isAdminCanEditSubject: boolean;
}

const PmObjectsWithoutStore = ({
  className = '',
  watchOnly,
  countriesSelectOptions,
  dangerClassesSelectOptions,
  formRef,
  hazardClassApplies,
  knmSystemType,
  objectKindsSelectOptions,
  objectSubKindsSelectOptions,
  objectTypesSelectOptions,
  riskCategoriesSelectOptions,
  subjectTypeOptions,
  isCanRefreshControlObject,
  isErknm,
  settings,
  isLinkedByImpossibility,
  isChangeSubjectTypeFromActionsAvailable,
  districtSelectOptions,
  knoSupervisionUuid,
  orderVersionId,
  reasonUuid,
  isAdminCanEditSubject,
}: IPmObjectsProps & IPmObjectsStoreProps) => {
  const push = formRef?.current.mutators.push;
  const remove = formRef?.current.mutators.remove;
  const created = formRef?.current.getState().values.created;
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const change = formRef?.current.change ? (formRef?.current.change as ICommonFormRef['current']['change']) : () => {};
  const getFieldState = formRef?.current.getFieldState
    ? (formRef?.current.getFieldState as ICommonFormRef['current']['getFieldState'])
    : () => {};
  const subjectFieldNames = usePmErknmSubjectFields({ name: '', isRemovePrefix: true });
  const { dispatchKnmSetLegalEntityData } = useKnmSetLegalEntityDataAction();

  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const openModal = useCallback(() => {
    setIsModalVisible(true);
  }, []);

  const closeModal = () => {
    setIsModalVisible(false);
  };
  const addNotice: (notice: INotification) => void = useCallback(
    (notice: INotification) => dispatch(addNoticeAction(notice)),
    [dispatch],
  );

  const isUseOnlyLowRiskInManualObject = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.KNM_VALIDATION_223_4_1_42_STARTS_FROM,
  );

  /**
   * Элемент "низкий риск"
   */
  const lowRisk = useMemo(() => {
    return riskCategoriesSelectOptions?.find(risk => risk.code === '6');
  }, [riskCategoriesSelectOptions]);

  /**
   * Переменная, отвечающая за блокировку кнопки "Добавить вручную"
   */
  const isDisabledButtonAddManual = useMemo(() => {
    const isDisabledByRisk = watchOnly || (isUseOnlyLowRiskInManualObject && !lowRisk);

    return isDisabledByRisk && !isChangeSubjectTypeFromActionsAvailable;
  }, [watchOnly, isUseOnlyLowRiskInManualObject, lowRisk, isChangeSubjectTypeFromActionsAvailable]);

  const addKnmObject = useCallback(() => {
    addObject(watchOnly, push, 'objectsPm', isUseOnlyLowRiskInManualObject && lowRisk ? lowRisk : undefined);
  }, [push, watchOnly, isUseOnlyLowRiskInManualObject, lowRisk]);

  const addFromErvk = useCallback(
    (ervkObjects: IErvkObjectsOfControl[], subjects: IErvkObjectsOfControlSubjectData[]) => {
      if (!watchOnly && push) {
        let id;

        const isNewFieldName = 'isOrganizationNew' as keyof IKnmFormSubject;

        subjects.forEach(async subject => {
          const currentSubject = formRef?.current.getFieldState(isNewFieldName)?.value;
          const isSubjectExistException = () => {
            if (currentSubject !== undefined) {
              const currentSubjectInn = getFieldState(subjectFieldNames.inn)?.value;
              const currentSubjectOgrn = getFieldState(subjectFieldNames.ogrn)?.value;
              const currentSubjectSnils = getFieldState(subjectFieldNames.snils)?.value;

              return !(
                (currentSubjectInn && currentSubjectInn === subject?.inn) ||
                (currentSubjectOgrn && currentSubjectOgrn === subject?.ogrn) ||
                (currentSubjectSnils && currentSubjectSnils === subject?.snils)
              );
            }

            return false;
          };

          const subjectTypeCode = mapSubjectTypeCodeToName(subject.subjectType);
          const isFIZ = subjectTypeCode === KnmSubjectTypesEnum.FIZ;
          const isIFIZ = subjectTypeCode === KnmSubjectTypesEnum.IFIZ;
          const isIP = subjectTypeCode === KnmSubjectTypesEnum.IP;

          const searchSubjectRegionId =
            subject?.locationRegionFiasUuid || subject?.locationRegionCode || subject?.regionCode;
          const foundSubjectRegion = await findRegionSelectOption(
            subject?.address ?? '',
            searchSubjectRegionId || undefined,
            districtSelectOptions,
          );

          if (isSubjectExistException()) {
            id = uuidv4();
          } else if (currentSubject === undefined) {
            const isSubjectAddressInFias = !!subject.locationIsFromFias;

            change(isNewFieldName, true);
            change(subjectFieldNames.inn, subject?.inn);
            change(subjectFieldNames.ogrn, subject?.ogrn);
            change(
              subjectFieldNames.organizationName,
              isFIZ || isIFIZ || isIP
                ? `${subject.surName} ${subject.firstName} ${subject.middleName}`
                : subject?.organizationFullName,
            );
            change(
              subjectFieldNames.subjectTypePm,
              subjectTypeOptions?.find(x => x.label === mapSubjectTypeCodeToName(subject?.subjectType)),
            );
            change(subjectFieldNames.rafpNza, subject?.rafp);
            change(subjectFieldNames.kpp, subject?.kpp);
            change(subjectFieldNames.snils, subject?.snils);
            change(subjectFieldNames.locationnotInFIAS, !isSubjectAddressInFias);
            change(subjectFieldNames.locationAddress, !isIP ? subject?.address : undefined);
            if (isSubjectAddressInFias) {
              change(subjectFieldNames.locationfiasRegion, foundSubjectRegion);
            } else {
              change(subjectFieldNames.locationregion, foundSubjectRegion);
            }
            change(subjectFieldNames.workAddress, !isIP ? subject?.address : undefined);
            change(subjectFieldNames.factualAddress, isIP ? subject.address : undefined);
            change(subjectFieldNames.ervkId, subject?.id);
            change(subjectFieldNames.document, subject?.identityDocumentNumber);
            if (subject?.nationality) {
              change(
                subjectFieldNames.country,
                countriesSelectOptions?.find(c => c.title === subject.nationality),
              );
            }
          }

          const relatedObjects = ervkObjects.filter(x => x.subjects.filter(s => s.inn === subject.inn)?.length);

          if (relatedObjects.length > 0 && !isIP && !subject?.address) {
            const firstObject = relatedObjects[0];
            const objectAddress =
              firstObject.address && firstObject.address.trim().length > 0 ? firstObject.address : undefined;
            const searchId =
              firstObject.locationRegionFiasUuid || firstObject.locationRegionCode || firstObject.regionCode;
            const foundRegion = await findRegionSelectOption(
              firstObject.address,
              searchId || undefined,
              districtSelectOptions,
            );

            if (objectAddress) {
              change(subjectFieldNames.locationAddress, objectAddress);
              if (foundRegion && !foundSubjectRegion) {
                change(subjectFieldNames.locationfiasRegion, foundRegion);
                change(subjectFieldNames.locationregion, foundRegion);
              }
            } else if (foundRegion && !foundSubjectRegion) {
              change(subjectFieldNames.locationfiasRegion, foundRegion);
              change(subjectFieldNames.locationregion, foundRegion);
            }
          }

          relatedObjects.forEach(object => {
            addErvkObject(
              object,
              subject?.id,
              dangerClassesSelectOptions,
              objectKindsSelectOptions,
              objectSubKindsSelectOptions,
              objectTypesSelectOptions,
              riskCategoriesSelectOptions,
              districtSelectOptions,
              push,
              'objectsPm',
            );
          });
        });

        if (id) {
          addNotice({
            renderContent: <>В паспорт можно добавить сведения только об одном контролируемом лице</>,
            noticeType: NoticeTypesEnum.ERROR,
            messageType: MessageTypesEnum.DELETE_REQUIREMENT,
            id,
          });
        } else {
          closeModal();
        }
      }
    },
    [
      push,
      watchOnly,
      objectTypesSelectOptions,
      objectKindsSelectOptions,
      objectSubKindsSelectOptions,
      riskCategoriesSelectOptions,
      dangerClassesSelectOptions,
    ],
  );

  const makeRemoveKnmObjectHandler = (index: number, organizationErvkIds: string[] | null | undefined) => () => {
    if (watchOnly || !remove) {
      return;
    }

    if (organizationErvkIds) {
      const subjectErvkId = formRef?.current.getState()?.values.ervkId;

      organizationErvkIds.forEach(orgErvkId => {
        const isLastObjectOfSubject =
          formRef?.current.getFieldState('objectsPm')?.value?.filter(obj => obj.organizationErvkId?.includes(orgErvkId))
            ?.length === 1;

        // Удаляем КЛ вместе с последним его ОБК — зеркало обработчика в ErknmObjects (КНМ).
        // Раньше при «Невозможности проведения...» вся ветка пропускалась и ОБК удалялся в одиночку.
        if (isLastObjectOfSubject && subjectErvkId === orgErvkId) {
          clearPmSubjectFields({ formRef, subjectFieldNames, withSubjectType: true });
          change(subjectFieldNames.isOrganizationNew);
          dispatchKnmSetLegalEntityData(undefined);
          change('isSubjectDeleted', true);
        }
      });
    }

    remove('objectsPm', index);
    change('isObjectDeleted', true);
  };

  /**
   * Обработчик изменения какого либо типа объекта
   */
  const onChangeObjectTypeChange = useCallback(
    (option: IDictionarySubjectTypeSelectOption) => {
      if (option && option?.value?.dictVersionId) {
        if (knoSupervisionUuid && (reasonUuid || orderVersionId)) {
          dispatch(
            dictionariesSubjectKindsItemGet(
              knoSupervisionUuid,
              reasonUuid,
              orderVersionId,
              option?.value?.dictVersionId,
            ),
          );
          dispatch(
            dictionariesSubjectSubKindsItemGet(
              knoSupervisionUuid,
              reasonUuid,
              orderVersionId,
              option?.value?.dictVersionId,
            ),
          );
        }
      }
    },
    [formRef, dispatch, knoSupervisionUuid, orderVersionId, reasonUuid],
  );

  const reasonsPm = formRef?.current.getFieldState('reasonsPm')?.value;
  const isPrescriptionCheck = reasonsPm?.[0]?.type?.name === KnmReasonTypeNameEnum.PRESCRIPTION_CHECK;

  return (
    <div className={className ? `${styles.Wrapper} ${className}` : styles.Wrapper} id={PM_OBJECTS}>
      <h2 className="shared-title__with-margin_medium">
        {getTitleFieldWithRedStar({
          label: 'Сведения об объектах контроля',
          field: `objectsPm`,
          isPm: true,
        })}
      </h2>
      <div className="shared-group-label" id={OBJECTS_BLOCK_ID}>
        Сведения об объектах&nbsp;
        <span className="shared-no-wrap-text">
          контроля
          {((reasonsPm?.[0]?.type?.name !== KnmReasonTypeNameEnum.IMPOSSIBILITY_OF_PREVIOUS &&
            reasonsPm?.[0]?.type?.name !== KnmReasonTypeNameEnum.PRESCRIPTION_CHECK) ||
            isAdminCanEditSubject) && (
            <>
              {isErknm && (
                <Button
                  className="shared-btn-add"
                  text="Добавить объект из ЕРВК"
                  type="button"
                  variant="link"
                  color="primary"
                  id="erknmObjectsAddButton"
                  disabled={watchOnly}
                  onClick={openModal}
                />
              )}
              {!lowRisk && isUseOnlyLowRiskInManualObject && isErknm ? (
                <Tooltip
                  placement="top"
                  type="info"
                  content={
                    <div className="shared-tooltip-info-content" style={{ width: '450px' }}>
                      Для выбранного вида контроля недоступны объекты с низкой категорией риска, которые добавляются
                      вручную
                    </div>
                  }
                >
                  <Button
                    className="shared-btn-add"
                    text="Добавить вручную"
                    type="button"
                    variant="link"
                    color="primary"
                    id="pmObjectsAddButton"
                    disabled={isDisabledButtonAddManual}
                    onClick={addKnmObject}
                  />
                </Tooltip>
              ) : (
                <Button
                  className="shared-btn-add"
                  text="Добавить вручную"
                  type="button"
                  variant="link"
                  color="primary"
                  id="pmObjectsAddButton"
                  disabled={isDisabledButtonAddManual}
                  onClick={addKnmObject}
                />
              )}
            </>
          )}
        </span>
      </div>
      {isPrescriptionCheck && <SelectPrescriptionNotice />}
      {isLinkedByImpossibility && formRef?.current.getState()?.values.isObjectDeleted && (
        <div className={styles.Yellow}>
          Для добавления сведений в раздел необходимо удалить и повторно добавить ссылку на паспорт
        </div>
      )}
      <KnmErvkObjectsModal
        watchOnly={watchOnly || isLinkedByImpossibility === true}
        dangerClassesSelectOptions={hazardClassApplies ? dangerClassesSelectOptions : undefined}
        riskCategoriesSelectOptions={!hazardClassApplies ? riskCategoriesSelectOptions : undefined}
        onClose={closeModal}
        visible={isModalVisible}
        onSubmit={addFromErvk}
        kindKnmCode={undefined}
      />

      <FormSpy subscription={{ errors: true, touched: true, submitFailed: true }}>
        {({ errors, touched, submitFailed }) => {
          const fieldError = getFieldArrayErrorMessage(errors?.objectsPm);

          if (fieldError && (touched?.objectsPm || submitFailed)) {
            return <p className="shared-input-error shared-row__small-margin">{fieldError}</p>;
          }

          return null;
        }}
      </FormSpy>

      <FieldArray name="objectsPm">
        {({ fields }: FieldArrayRenderProps<IKnmErknmFormObject, HTMLElement>) => {
          if (!fields.length) {
            return null;
          }

          return (
            <div className={styles.ObjectsList}>
              {fields.map((name, index) => {
                const fieldValue = fields.value?.[index];
                const id = getObjectBlockId(fieldValue.listId);
                const isErvkObject = !!fieldValue.ervkId;

                return (
                  <KnmObjectItem
                    key={id}
                    id={id}
                    created={created}
                    index={index}
                    hazardClassApplies={hazardClassApplies}
                    formRef={formRef}
                    name={name}
                    knmSystemType={knmSystemType}
                    value={fieldValue}
                    objectTypesSelectOptions={objectTypesSelectOptions}
                    objectKindsSelectOptions={objectKindsSelectOptions}
                    objectSubKindsSelectOptions={objectSubKindsSelectOptions}
                    riskCategoriesSelectOptions={riskCategoriesSelectOptions}
                    dangerClassesSelectOptions={dangerClassesSelectOptions}
                    districtSelectOptions={districtSelectOptions}
                    countriesSelectOptions={countriesSelectOptions}
                    objectTypeChangeCallback={onChangeObjectTypeChange}
                    objectKindSelectOptionsSelectorFunction={getKnmErknmSubjectKindsSelectOptions}
                    objectSubKindSelectOptionsSelectorFunction={getKnmErknmSubjectSubKindsSelectOptions}
                    isWatchOnly={
                      watchOnly ||
                      isLinkedByImpossibility === true ||
                      (isErvkObject && isChangeSubjectTypeFromActionsAvailable)
                    }
                    onRemove={makeRemoveKnmObjectHandler(index, fieldValue?.organizationErvkId)}
                    isErknm={false}
                    isPm={true}
                    isCanRefreshControlObject={isCanRefreshControlObject}
                    isLinkedByImpossibility={isLinkedByImpossibility}
                    reasonType={reasonsPm?.[0]?.reason?.type}
                    isHasNonErvkOrganizations={!formRef?.current.getState()?.values.ervkId}
                    pmOrganization={formRef?.current.getState()?.values}
                    isRemoveAvailable={!watchOnly}
                  />
                );
              })}
            </div>
          );
        }}
      </FieldArray>
    </div>
  );
};

const mapStateToProps = (state: IRootState): IPmObjectsStoreProps => {
  const isErknm = getLawInterface(state) === LawInterfacesEnum.FZ_248;

  return {
    dangerClassesSelectOptions: getKnmErknmDangerClassesSelectOptions(state),
    formRef: getKnmFormRef(state),
    hazardClassApplies: getKnoSupervisionHazardClassApplies(state),
    knmSystemType: getKnmSystemType(state),
    objectKindsSelectOptions: getKnmErknmSubjectKindsSelectOptions(state),
    objectSubKindsSelectOptions: getKnmErknmSubjectSubKindsSelectOptions(state),
    objectTypesSelectOptions: getKnmErknmSubjectTypesSelectOptions(state),
    riskCategoriesSelectOptions: getKnmErknmRiskCategoriesSelectOptions(state),
    subjectTypeOptions: getTargetEntitySelectOptions(state),
    countriesSelectOptions: getCountriesSelectOptions(state),
    isCanRefreshControlObject: getKnmIsCanRefreshControlObject(state),
    settings: getAdminFetchSettings(state),
    isChangeSubjectTypeFromActionsAvailable: getKnmIsChangeSubjectTypeFromActionsAvailable(state),
    isErknm,
    districtSelectOptions: getDistrictSelectOptions(state),
    knoSupervisionUuid: getKnmKnoSupervisionUuid(state),
    orderVersionId: getKnmOrderVersionId(state),
    reasonUuid:
      getKnmPmReasonsFormValue(state)
        ?.map(x => x.reason?.ervkUuid)
        ?.find(uuid => uuid) || undefined,
    isAdminCanEditSubject: getKnmAdminCanEditSubject(state),
  };
};

export const PmObjects = connect(mapStateToProps)(PmObjectsWithoutStore);
